import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Service } from '@turizi/shared';
import { useServiceBookingsByService } from '@/hooks/api-hooks';
import DatePickerCustom from '@/components/custom-date-picker';
import SearchInput from '@/components/search-input';
import ServiceBookingsList from '@/components/service-bookings-list';
import { SkeletonListWithFilters } from '@/components/snippets';
import dayjs from 'dayjs';

interface Props {
    service: Service;
}

export default function ServiceBookingsTab({ service }: Props) {
    const { t } = useTranslation();

    // Default to a 9-month window around today: 3 months ago to 6 months in the future
    const [dateRange, setDateRange] = useState<[string, string]>([
        dayjs().subtract(3, 'month').format('YYYY-MM-DD'),
        dayjs().add(6, 'month').format('YYYY-MM-DD')
    ]);
    const [searchQuery, setSearchQuery] = useState('');

    const {
        data: bookingsRes,
        isLoading,
        error
    } = useServiceBookingsByService({
        service_id: service.id,
        start_day_from: dateRange[0] || undefined,
        start_day_to: dateRange[1] || undefined
    });

    const bookings = bookingsRes?.data || [];

    const filteredBookings = useMemo(() => {
        let list = bookings;
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            list = list.filter((b) => {
                if (String(b.id).includes(query)) return true;
                if (b.reservation_id && String(b.reservation_id).includes(query)) return true;
                if (b.service_order_id && String(b.service_order_id).includes(query)) return true;

                // client person/business name matching
                const clientBusinessName = b.reservation?.client_business?.name?.toLowerCase() || '';
                if (clientBusinessName.includes(query)) return true;

                // pax group & person name/phone matching
                if (b.pax_groups?.some(bg => {
                    const groupName = bg.pax_group?.name?.toLowerCase() || '';
                    if (groupName.includes(query)) return true;
                    return bg.pax_group?.persons?.some(p => {
                        const fullName = p.name?.toLowerCase() || '';
                        return fullName.includes(query) || p.phone?.includes(query);
                    });
                })) return true;

                // provider name matching
                if (b.selected_provider?.name?.toLowerCase().includes(query)) return true;

                // coordinator name matching
                if (b.coordinator?.name?.toLowerCase().includes(query)) return true;

                return false;
            });
        }
        return list;
    }, [bookings, searchQuery]);

    if (isLoading && bookings.length === 0) {
        return (
            <div className="p-4">
                <SkeletonListWithFilters />
            </div>
        );
    }

    return (
        <div className="panel-content space-y-6">
            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                    {error.message}
                </div>
            )}

            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                    <DatePickerCustom
                        hideLabel
                        className="font-bold"
                        clearable={false}
                        value={dateRange}
                        onChange={(val) => setDateRange(val)}
                        loading={isLoading}
                    />
                    <SearchInput
                        className="flex-1 sm:w-64"
                        onSearch={(text) => setSearchQuery(text)}
                        placeholder={t('search')}
                    />
                </div>
            </div>

            {filteredBookings.length === 0 ? (
                <div className="text-center py-10 bg-white border border-gray-100 rounded-lg shadow-sm">
                    <p className="text-gray-500 text-sm">{t('no_service_bookings_found')}</p>
                </div>
            ) : (
                <ServiceBookingsList
                    bookings={filteredBookings}
                    editable={true}
                    showBookingId={true}
                    showByCategory={false}
                    showByService={false}
                    showServiceOrder={true}
                    showReservation={true}
                    showClientName={true}
                    showPrice={true}
                    showCost={true}
                    showPaymentStatus={true}
                    showPaxs={true}
                    showPhone={true}
                    showPaxName={true}
                    showHour={true}
                    showProvider={true}
                    showCoordinator={true}
                    showHeader={false}
                />
            )}
        </div>
    );
}
